// qchat_fe/src/Component/SideData/Overview/AiAgentWorkFlow/OAuthConnections.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaPlug,
  FaSyncAlt,
  FaInfoCircle,
  FaLink,
  FaUnlink,
} from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";

import ReusablePopup from "../../../../ReusablePopup";
import {
  listConnections,
  revokeConnection,
  startOAuthFlow,
} from "./utils/oauthApi";
import styles from "./OAuthConnections.module.css";

import ScopeTabs from "./ScopeTabs";
import ResourceScopeBadge from "./utils/ResourceScopeBadge";
import {
  attachScope,
  detachScope,
  listAttachments,
  getAttachmentsByResource,
} from "./utils/scopeAttachmentsApi";
import {
  classifyGuardRefusal,
  formatAttachedAppChips,
} from "./utils/scopeAttachmentValidation";

const SCOPE = {
  APP: "app",
  TENANT: "tenant",
};

const SCOPE_LABELS = {
  "openid": "Sign in with Google",
  "https://www.googleapis.com/auth/userinfo.email": "See your email address",
  "https://www.googleapis.com/auth/spreadsheets": "Google Sheets",
  "https://www.googleapis.com/auth/gmail.send": "Send email (Gmail)",
  "https://www.googleapis.com/auth/calendar.events": "Calendar events",
};

function labelForScope(scope) {
  return SCOPE_LABELS[scope] || scope;
}

function relativeTime(iso) {
  if (!iso) return "never";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffSec = Math.max(0, Math.floor((now - then) / 1000));
    if (diffSec < 60) return "just now";
    const min = Math.floor(diffSec / 60);
    if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
    const mo = Math.floor(day / 30);
    if (mo < 12) return `${mo} month${mo === 1 ? "" : "s"} ago`;
    const yr = Math.floor(mo / 12);
    return `${yr} year${yr === 1 ? "" : "s"} ago`;
  } catch {
    return iso;
  }
}

const connectionKey = (conn) =>
  `${conn.provider}::${conn.external_account_id}`;

const OAuthConnections = ({ appData, controlledScope, hideScopeTabs }) => {
  const region = appData?.region?.toLowerCase();
  const appName = appData?.app_name;

  const isGlobalContext = !appName;

  const [internalTab, setInternalTab] = useState(SCOPE.TENANT);
  const activeTab = controlledScope || internalTab;

  const [tenantConnections, setTenantConnections] = useState([]);
  const [appConnections, setAppConnections] = useState([]);
  const [providers, setProviders] = useState([]);
  const [attachedIds, setAttachedIds] = useState(() => new Set());
  const [attachmentsByConnection, setAttachmentsByConnection] = useState(
    () => new Map(),
  );
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const [connectingProvider, setConnectingProvider] = useState(null);

  const [popup, setPopup] = useState({
    isOpen: false,
    heading: "",
    subtext: "",
    paragraph: "",
    confirmText: "OK",
    cancelText: "",
    icon: null,
    iconColor: "#4f46e5",
    iconBG: "#e0e7ff",
    onConfirm: null,
  });

  const closePopup = () =>
    setPopup((p) => ({ ...p, isOpen: false, onConfirm: null }));

  const openAlert = ({
    heading,
    subtext = "",
    paragraph = "",
    icon = null,
    iconColor = "#16a34a",
    iconBG = "#dcfce7",
  }) => {
    setPopup({
      isOpen: true,
      heading,
      subtext,
      paragraph,
      confirmText: "OK",
      cancelText: "",
      icon,
      iconColor,
      iconBG,
      onConfirm: closePopup,
    });
  };

  const openConfirm = ({
    heading,
    subtext = "",
    paragraph = "",
    confirmText = "Confirm",
    cancelText = "Cancel",
    icon = null,
    iconColor = "#dc2626",
    iconBG = "#fee2e2",
    onConfirm,
  }) => {
    setPopup({
      isOpen: true,
      heading,
      subtext,
      paragraph,
      confirmText,
      cancelText,
      icon,
      iconColor,
      iconBG,
      onConfirm,
    });
  };

  const openGuardPopup = ({ refusal }) => {
    const BodyNode = (
      <div style={{ textAlign: "left", lineHeight: 1.6 }}>
        <div style={{ marginBottom: 8 }}>{refusal.message}</div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            marginTop: 6,
          }}
        >
          {formatAttachedAppChips(
            refusal.apps.map((a) => ({ app_name: a })),
          ).map((chip) => (
            <button
              key={chip.app_name}
              type="button"
              onClick={async () => {
                try {
                  await detachScope({
                    region,
                    appName: chip.app_name,
                    resourceType: "oauth_connection",
                    tenantResourceId: refusal.attachKey,
                  });
                  openAlert({
                    heading: "Detached",
                    paragraph: `${chip.app_name} has been detached from this connection.`,
                    icon: <FaCheckCircle size={28} />,
                  });
                  closePopup();
                  setRefreshTick((t) => t + 1);
                } catch (e) {
                  openAlert({
                    heading: "Couldn't detach",
                    paragraph: e.message,
                    icon: <FaTimesCircle size={28} />,
                    iconColor: "#dc2626",
                    iconBG: "#fee2e2",
                  });
                }
              }}
              style={{
                padding: "4px 10px",
                fontSize: 12,
                borderRadius: 4,
                border: "1px solid #c4b5fd",
                background: "#f5f3ff",
                color: "#6b46c1",
                cursor: "pointer",
              }}
            >
              Detach {chip.app_name}
            </button>
          ))}
        </div>
      </div>
    );

    setPopup({
      isOpen: true,
      heading: "This connection is attached to other apps",
      subtext: "",
      paragraph: BodyNode,
      confirmText: "OK",
      cancelText: "",
      icon: <FaExclamationTriangle size={28} />,
      iconColor: "#d97706",
      iconBG: "#fef3c7",
      onConfirm: closePopup,
    });
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!region) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        const [appConnData, tenantConnData, attachResp] = await Promise.all([
          appName
            ? listConnections({ appName, region, scope: "app" })
            : Promise.resolve({ connections: [] }),
          listConnections({ appName: undefined, region, scope: "tenant" }),
          appName
            ? listAttachments({
                region,
                appName,
                resourceType: "oauth_connection",
              })
            : getAttachmentsByResource({
                region,
                resourceType: "oauth_connection",
              }).then((map) => {
                const attachments = [];
                for (const bucket of map.values()) {
                  for (const a of bucket.attachments) attachments.push(a);
                }
                return { attachments, count: attachments.length };
              }),
        ]);

        if (cancelled) return;

        setProviders(appConnData.providers || tenantConnData.providers || []);
        
        const rawApp = appConnData.connections || [];
        const rawTenant = tenantConnData.connections || [];

        setAppConnections(rawApp);
        setTenantConnections(rawTenant);

        const ids = new Set();
        const grouped = new Map();
        for (const row of attachResp.attachments || []) {
          const rid = row.tenant_resource_id;
          if (!rid) continue;
          if (!grouped.has(rid)) {
            grouped.set(rid, { app_names: [], attachments: [] });
          }
          const bucket = grouped.get(rid);
          bucket.attachments.push(row);
          if (row.app_name && !bucket.app_names.includes(row.app_name)) {
            bucket.app_names.push(row.app_name);
          }
          if (appName && row.app_name === appName) ids.add(rid);
        }
        for (const bucket of grouped.values()) bucket.app_names.sort();

        setAttachedIds(ids);
        setAttachmentsByConnection(grouped);
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [appName, region, refreshTick, isGlobalContext]);

  const handleConnect = useCallback(
    async (provider) => {
      if (connectingProvider) return;
      setConnectingProvider(provider.name);

      try {
        // ⭐ Connect scope is derived from the active tab. No inner
        // picker — the outer ScopeTabs already decided which tier the
        // user is working in. On the Tenant tab we create a tenant
        // grant; on the App tab we create an app-scoped grant.
        const effectiveScope =
          activeTab === SCOPE.TENANT || isGlobalContext
            ? SCOPE.TENANT
            : SCOPE.APP;

        try {
          sessionStorage.setItem(
            "oauth_return_to",
            JSON.stringify({
              region,
              appName,
              connectScope: effectiveScope,
            }),
          );
        } catch (_) {}

        const authorizeUrl = await startOAuthFlow({
          appName: effectiveScope === SCOPE.APP ? appName : undefined,
          region,
          provider: provider.name,
          scope: effectiveScope,
        });

        window.location.href = authorizeUrl;
      } catch (err) {
        setConnectingProvider(null);
        openAlert({
          heading: "Couldn't start the connection",
          paragraph: err.message,
          icon: <FaExclamationTriangle size={28} />,
          iconColor: "#d97706",
          iconBG: "#fef3c7",
        });
      }
    },
    [appName, region, connectingProvider, activeTab, isGlobalContext],
  );

  const performRevoke = async (conn) => {
    try {
      await revokeConnection({
        appName: appName || "",
        region,
        provider: conn.provider,
        externalAccountId: conn.external_account_id,
        scope: conn.scope || SCOPE.APP,
      });
      openAlert({
        heading: "Disconnected",
        paragraph:
          `${conn.display_name || conn.external_account_id} has been ` +
          `disconnected. Any skills that used it will need to reconnect ` +
          `this account before they can call Google again.`,
        icon: <FaCheckCircle size={28} />,
        iconColor: "#16a34a",
        iconBG: "#dcfce7",
      });
      setRefreshTick((t) => t + 1);
    } catch (err) {
      openAlert({
        heading: "Couldn't disconnect",
        paragraph: err.message,
        icon: <FaTimesCircle size={28} />,
        iconColor: "#dc2626",
        iconBG: "#fee2e2",
      });
    }
  };

  const handleRevoke = (conn) => {
    openConfirm({
      heading: `Disconnect ${
        conn.display_name || conn.external_account_id
      }?`,
      subtext: `Provider: ${conn.provider}`,
      paragraph:
        "The connection will be removed and the stored access tokens " +
        "deleted. Any skill or form that uses this connection will stop " +
        "working until an account is reconnected.\n\n" +
        "This action cannot be undone.",
      confirmText: "Disconnect",
      cancelText: "Cancel",
      icon: <RiDeleteBin6Line size={28} />,
      iconColor: "#dc2626",
      iconBG: "#fee2e2",
      onConfirm: async () => {
        closePopup();
        await performRevoke(conn);
      },
    });
  };

  const handleAttachConnection = async (conn) => {
    try {
      await attachScope({
        region,
        appName,
        resourceType: "oauth_connection",
        tenantResourceId: connectionKey(conn),
      });
      openAlert({
        heading: "Attached",
        paragraph: "This connection is now available to this app.",
        icon: <FaCheckCircle size={28} />,
      });
      setRefreshTick((t) => t + 1);
    } catch (err) {
      openAlert({
        heading: "Couldn't attach",
        paragraph: err.message,
        icon: <FaTimesCircle size={28} />,
        iconColor: "#dc2626",
        iconBG: "#fee2e2",
      });
    }
  };

  const handleDetachConnection = (conn) => {
    openConfirm({
      heading: "Detach this connection from this app?",
      subtext: `Account: ${conn.display_name || conn.external_account_id}`,
      paragraph:
        "The tenant grant is not revoked — the account stays connected " +
        "for every other app that uses it. This app will stop being able " +
        "to act on its behalf. If this app needs to act on the account " +
        "again, either re-attach this connection or connect it " +
        "independently from this app's Connections tab.",
      confirmText: "Detach",
      cancelText: "Cancel",
      icon: <FaExclamationTriangle size={28} />,
      iconColor: "#d97706",
      iconBG: "#fef3c7",
      onConfirm: async () => {
        closePopup();
        try {
          await detachScope({
            region,
            appName,
            resourceType: "oauth_connection",
            tenantResourceId: connectionKey(conn),
          });
          openAlert({
            heading: "Detached",
            paragraph: "This app no longer has access to this connection.",
            icon: <FaCheckCircle size={28} />,
          });
          setRefreshTick((t) => t + 1);
        } catch (err) {
          openAlert({
            heading: "Couldn't detach",
            paragraph: err.message,
            icon: <FaTimesCircle size={28} />,
            iconColor: "#dc2626",
            iconBG: "#fee2e2",
          });
        }
      },
    });
  };

  const renderConnectionCard = (conn, { isTenantTab }) => {
    const providerMeta = providers.find((p) => p.name === conn.provider);
    const attachKey = connectionKey(conn);
    const bucket = attachmentsByConnection.get(attachKey);
    const appNames = bucket?.app_names || [];
    const isAttachedByMe = attachedIds.has(attachKey);
    const globalAdmin = isGlobalContext;

    const hasAppScopedOverride = appConnections.some(
      (c) =>
        c.provider === conn.provider &&
        c.external_account_id === conn.external_account_id,
    );

    let badgeKind;
    if ((conn.scope || SCOPE.APP) === SCOPE.APP) {
      badgeKind = "app";
    } else if (globalAdmin) {
      badgeKind =
        appNames.length > 0
          ? "tenant-detached-blocked"
          : "tenant-unattached";
    } else {
      badgeKind = isAttachedByMe ? "tenant-attached" : "tenant-available";
    }

    const canRevoke =
      (conn.scope || SCOPE.APP) === SCOPE.APP || globalAdmin;

    const showAttachDetach =
      isTenantTab && !globalAdmin && !hasAppScopedOverride;

    return (
      <div
        key={`${conn.provider}__${conn.external_account_id}__${conn.scope || "app"}`}
        className={styles.connectionCard}
      >
        <div className={styles.connectionIconWrap}>
          {providerMeta?.icon_url ? (
            <img
              src={providerMeta.icon_url}
              alt={conn.provider}
              className={styles.connectionIcon}
            />
          ) : (
            <FaPlug size={22} className={styles.connectionIconFallback} />
          )}
        </div>

        <div className={styles.connectionBody}>
          <div className={styles.connectionLine1}>
            <ResourceScopeBadge
              kind={badgeKind}
              count={
                globalAdmin && appNames.length > 0
                  ? appNames.length
                  : undefined
              }
              onClick={
                globalAdmin && appNames.length > 0
                  ? () =>
                      openGuardPopup({
                        refusal: {
                          kind: "attached-in-use",
                          apps: appNames,
                          refs: null,
                          message: `This shared connection is attached to ${appNames.length} app${appNames.length === 1 ? "" : "s"}: ${appNames.join(", ")}.`,
                          attachKey,
                        },
                      })
                  : undefined
              }
            />
            <span
              className={styles.connectionAccount}
              style={{ marginLeft: 8 }}
            >
              {conn.email ||
                conn.display_name ||
                conn.external_account_id}
            </span>
            <span className={styles.badgeApp}>{conn.provider}</span>
          </div>

          {conn.email &&
            conn.display_name &&
            conn.display_name !== conn.email && (
              <div className={styles.connectionLine2}>
                {conn.display_name}
              </div>
            )}

          <div className={styles.connectionLine3}>
            <span>Connected {relativeTime(conn.granted_at)}</span>
            {conn.last_refreshed_at && (
              <>
                <span className={styles.dot}>·</span>
                <span>Refreshed {relativeTime(conn.last_refreshed_at)}</span>
              </>
            )}
          </div>

          {Array.isArray(conn.scopes_granted) &&
            conn.scopes_granted.length > 0 && (
              <div className={styles.scopesRow}>
                {conn.scopes_granted.map((s) => (
                  <span key={s} className={styles.scopeChip}>
                    {labelForScope(s)}
                  </span>
                ))}
              </div>
            )}

          {showAttachDetach && (
            <div
              style={{
                marginTop: 12,
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              {isAttachedByMe ? (
                <button
                  type="button"
                  onClick={() => handleDetachConnection(conn)}
                  style={{
                    padding: "6px 14px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#c05621",
                    backgroundColor: "#fffaf0",
                    border: "1px solid #fbd38d",
                    borderRadius: 6,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <FaUnlink size={12} /> Detach from this app
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleAttachConnection(conn)}
                  style={{
                    padding: "6px 14px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#2b6cb0",
                    backgroundColor: "#ebf8ff",
                    border: "1px solid #90cdf4",
                    borderRadius: 6,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <FaLink size={12} /> Attach to this app
                </button>
              )}
            </div>
          )}
        </div>

        <div className={styles.connectionActions}>
          {canRevoke && (
            <button
              type="button"
              className={styles.disconnectBtn}
              onClick={() => handleRevoke(conn)}
              title="Disconnect this account"
            >
              <RiDeleteBin6Line size={16} />
              <span>Disconnect</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingBlock}>
          <FaSyncAlt className={styles.spin} size={16} />
          <span>Loading connections…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorBlock}>
          <FaTimesCircle size={16} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  const activeConnections =
    activeTab === SCOPE.TENANT ? tenantConnections : appConnections;
  const tenantCount = tenantConnections.length;
  const appCount = appConnections.length;
  const hasAppOverride = appConnections.length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div>
          <h2 className={styles.title}>
            {isGlobalContext ? "Tenant Connections" : "Connected Accounts"}
          </h2>
          <p className={styles.subtitle}>
            {isGlobalContext
              ? "OAuth accounts shared across every app on this tenant. Every app can use these grants for its skills and forms."
              : "Accounts this app can act on behalf of. Skills and forms use the connection you authorize here — end users never see Google."}
          </p>
        </div>
      </div>

      {!hideScopeTabs && (
        <ScopeTabs
          activeTab={activeTab}
          onTabChange={setInternalTab}
          tenantCount={tenantCount}
          appCount={appCount}
          resourceNoun="connection"
          overriddenByAppLevel={
            activeTab === SCOPE.TENANT && hasAppOverride && !isGlobalContext
          }
          attachCount={attachedIds.size}
          disabled={loading}
          compact={false}
          hiddenTabs={isGlobalContext ? ["app"] : []}
        />
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 12,
        }}
      >
        <button
          onClick={() => setRefreshTick((t) => t + 1)}
          disabled={loading}
          title="Reload connections"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: 12,
            color: "#475569",
            opacity: loading ? 0.6 : 1,
          }}
        >
          <FaSyncAlt size={11} /> Reload
        </button>
      </div>

      {activeConnections.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            {activeTab === SCOPE.TENANT
              ? "Tenant connections"
              : "App connections"}
          </div>
          <div className={styles.connectionList}>
            {activeConnections.map((conn) =>
              renderConnectionCard(conn, {
                isTenantTab: activeTab === SCOPE.TENANT,
              }),
            )}
          </div>
        </div>
      )}

      {activeConnections.length === 0 && (
        <div className={styles.emptyHint} style={{ marginBottom: 16 }}>
          {activeTab === SCOPE.TENANT
            ? isGlobalContext
              ? "No tenant-scoped connections yet."
              : "Your tenant admin hasn't published any shared connections yet. You can create your own from the App Level tab."
            : "No app-scoped connections yet. Connect an account below."}
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          {activeConnections.length > 0
            ? "Add another connection"
            : "Connect an account"}
        </div>

        {providers.length === 0 && (
          <div className={styles.emptyHint}>
            No providers are registered yet.
          </div>
        )}

        <div className={styles.providerGrid}>
          {providers.map((p) => {
            const isConnecting = connectingProvider === p.name;
            const anyConnecting = connectingProvider !== null;
            return (
              <div key={p.name} className={styles.providerCard}>
                <div className={styles.providerIconWrap}>
                  {p.icon_url ? (
                    <img
                      src={p.icon_url}
                      alt={p.display_name || p.name}
                      className={styles.providerIcon}
                    />
                  ) : (
                    <FaPlug size={22} className={styles.providerIconFallback} />
                  )}
                </div>

                <div className={styles.providerBody}>
                  <div className={styles.providerName}>
                    {p.display_name || p.name}
                  </div>
                  <div className={styles.providerScopes}>
                    {Array.isArray(p.default_scopes) &&
                      p.default_scopes.length > 0 && (
                        <>
                          {p.default_scopes.slice(0, 3).map((s) => (
                            <span key={s} className={styles.scopeChip}>
                              {labelForScope(s)}
                            </span>
                          ))}
                          {p.default_scopes.length > 3 && (
                            <span className={styles.scopeChipMuted}>
                              +{p.default_scopes.length - 3} more
                            </span>
                          )}
                        </>
                      )}
                  </div>
                </div>

                <button
                  type="button"
                  className={styles.connectBtn}
                  onClick={() => handleConnect(p)}
                  disabled={anyConnecting}
                  style={{
                    opacity: anyConnecting ? 0.6 : 1,
                    cursor: anyConnecting ? "wait" : "pointer",
                  }}
                >
                  {isConnecting
                    ? "Starting…"
                    : `Connect ${p.display_name || p.name}`}
                </button>
              </div>
            );
          })}
        </div>

        {providers.length > 0 && (
          <div className={styles.footnote}>
            <FaInfoCircle size={12} />
            <span>
              You'll be taken to{" "}
              {providers[0]?.display_name || "the provider"} to authorize. End
              users on your website never see this screen — the account you
              connect here acts on your behalf.
            </span>
          </div>
        )}
      </div>

      <ReusablePopup
        isOpen={popup.isOpen}
        onClose={closePopup}
        onConfirm={popup.onConfirm}
        icon={popup.icon}
        iconColor={popup.iconColor}
        iconBG={popup.iconBG}
        heading={popup.heading}
        subtext={popup.subtext}
        paragraph={popup.paragraph}
        cancelText={popup.cancelText}
        confirmText={popup.confirmText}
        showCancel={!!popup.cancelText}
        showConfirm={true}
      />
    </div>
  );
};

export default OAuthConnections;